import { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Eye, EyeOff, Loader2, Quote } from 'lucide-react';
import { SlideOver } from '@/components/pdo/SlideOver';
import {
  creativeStrategyApi,
  type StrategyDocketItemDetail,
  type StrategyDocketUsage,
  type StrategyExemplarRecord,
} from '@/services/api';
import { ExemplarCard, ExemplarLightbox } from './Exemplars';
import { ConceptReviews } from '../ConceptReviews';
import { AnglesTable, HandoffsTable, UnavailableMembers } from './Usage';
import { ClassBadge, Chip, Empty, ErrorNote, Field, Loading, Section, Stat, WarningBanner, toText } from './shared';

function facetChips(d: Record<string, unknown>): Array<[string, string]> {
  const out: Array<[string, string]> = [];
  const push = (label: string, v: unknown) => {
    const text = toText(v);
    if (text) out.push([label, text]);
  };
  const facets = (d.facets as Record<string, unknown> | undefined) || {};
  push('layout', d.layout_archetype ?? facets.layout);
  push('composition', d.composition);
  push('category', d.macro_category);
  if (Array.isArray(d.aesthetic_tags)) d.aesthetic_tags.forEach((t) => push('aesthetic', t));
  const vs = d.visual_style ?? facets.visual_style;
  if (vs && typeof vs === 'object' && !Array.isArray(vs)) {
    Object.entries(vs as Record<string, unknown>).forEach(([k, v]) => push(k, v));
  } else push('visual style', vs);
  if (d.product_count !== undefined && d.product_count !== null) push('products', d.product_count);
  return out;
}

export function DocketItemDrawer({
  docketId,
  itemId,
  version,
  clientUid,
  usage,
  usageLoading,
  onOpenItem,
  onClose,
  onSetItem,
}: {
  docketId: string;
  itemId: string | null;
  version?: string | null;
  clientUid?: string | null;
  usage: StrategyDocketUsage | null;
  usageLoading: boolean;
  onOpenItem: (id: string) => void;
  onClose: () => void;
  onSetItem: (itemId: string, body: { hidden: boolean; note?: string | null }) => Promise<void>;
}) {
  const [detail, setDetail] = useState<StrategyDocketItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [lightbox, setLightbox] = useState<StrategyExemplarRecord | null>(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    setError(null);
    try {
      const d = await creativeStrategyApi.docketItem(docketId, itemId, { version, clientUid });
      setDetail(d);
      setNote(d.item.note ?? '');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [docketId, itemId, version, clientUid]);

  useEffect(() => {
    setDetail(null);
    setLightbox(null);
    load();
  }, [load]);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  async function save(hidden: boolean, nextNote: string) {
    if (!itemId) return;
    setSaving(true);
    try {
      await onSetItem(itemId, { hidden, note: nextNote || null });
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  const item = detail?.item;
  const d = (item?.data || {}) as Record<string, unknown>;
  const slots = (d.slots as string[] | undefined) ?? [];
  const levels = (d.levels as string[] | undefined) ?? [];
  const facets = item?.entity_class === 'creative_pattern' ? facetChips(d) : [];
  const resolvedExemplars = detail?.exemplars.filter((e) => !e.missing).length ?? 0;

  const itemUsage = itemId ? usage?.items[itemId] : undefined;
  const angles = itemId ? (usage?.angles || []).filter((a) => a.template_id === itemId || a.evidence_ids.includes(itemId)) : [];
  const handoffs = itemId ? (usage?.handoffs || []).filter((h) => h.template_id === itemId) : [];

  return (
    <SlideOver
      open={!!itemId}
      onClose={onClose}
      width="xl"
      title={item?.canonical_name || itemId || ''}
      subtitle={
        <span className="font-mono">
          {itemId} · {detail?.version ?? version ?? '…'}
          {detail?.client_uid ? ` · overlay ${detail.client_uid}` : ''}
        </span>
      }
    >
      {loading && !detail && <div className="px-5"><Loading label="Loading item…" /></div>}
      {error && <div className="px-5"><ErrorNote error={error} /></div>}
      {item && detail && (
        <div className="px-5 py-4 space-y-6">
          <div className="flex flex-wrap items-center gap-1.5">
            <ClassBadge entityClass={item.entity_class} />
            <Chip mono>{item.scope}</Chip>
            {levels.map((l) => <Chip key={l} tone="navy">{l}</Chip>)}
            {item.hidden && <Chip tone="amber"><EyeOff size={10} /> hidden from briefs</Chip>}
            {item.in_client_graph != null && (
              <Chip tone={item.in_client_graph ? 'emerald' : 'slate'}>
                in client graph: {String(item.in_client_graph)}{item.graph_concept ? ` · ${item.graph_concept}` : ''}
              </Chip>
            )}
            {item.client_runs_it != null && <Chip tone={item.client_runs_it ? 'emerald' : 'slate'}>client runs it: {String(item.client_runs_it)}</Chip>}
            <button
              type="button"
              disabled={saving}
              onClick={() => save(!item.hidden, note)}
              className={clsx(
                'ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg border disabled:opacity-50',
                item.hidden ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
              )}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : item.hidden ? <Eye size={12} /> : <EyeOff size={12} />}
              {item.hidden ? 'Unhide' : 'Hide'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {item.entity_class === 'market_concept' ? (
              <>
                <Stat label="Review mentions" value={item.support ?? '—'} />
                <Stat label="Sentiment" value={toText(d.sentiment) || '—'} />
                <Stat label="Concept type" value={toText(d.concept_type) || '—'} />
                <Stat label="Cited by angles" value={itemUsage?.cited ?? (usage ? 0 : '…')} />
              </>
            ) : (
              <>
                <Stat label={item.entity_class === 'creative_pattern' ? 'Brands' : 'Advertisers'} value={item.advertisers ?? '—'} />
                <Stat label="Ads" value={item.support ?? '—'} />
                <Stat label="Median days running" value={item.median_days_running ?? '—'} />
                <Stat
                  label="Cited / executed"
                  value={usage ? `${itemUsage?.cited ?? 0} / ${itemUsage?.handoffs ?? 0}` : usageLoading ? '…' : '—'}
                />
              </>
            )}
          </div>

          {item.entity_class === 'market_concept' && (
            <>
              {typeof d.decision_factor === 'string' && <Field label="Decision factor">{d.decision_factor}</Field>}
              {item.evidence.length > 0 && (
                <Field label="Review evidence">
                  <div className="space-y-2">
                    {item.evidence.map((ev, i) => (
                      <blockquote key={i} className="flex gap-2 text-slate-700 italic border-l-2 border-slate-200 pl-3">
                        <Quote size={14} className="text-slate-300 flex-shrink-0 mt-0.5" />
                        <span>{ev}</span>
                      </blockquote>
                    ))}
                  </div>
                </Field>
              )}
              <Section title="Reviews behind this concept (Amazon, internal only)">
                <ConceptReviews
                  load={() => creativeStrategyApi.docketItemReviews(docketId, item.resolved_id, version)}
                  deps={[docketId, item.resolved_id, version]}
                />
              </Section>
            </>
          )}

          {item.entity_class === 'creative_strategy' && (
            <>
              {typeof d.strategy_type === 'string' && <Field label="Strategy type"><Chip tone="navy">{d.strategy_type}</Chip></Field>}
              {typeof d.argument === 'string' && <Field label="Argument">{d.argument}</Field>}
              {item.evidence.length > 0 && item.evidence[0] !== d.argument && (
                <Field label="Evidence">{item.evidence.join(' · ')}</Field>
              )}
            </>
          )}

          {item.entity_class === 'creative_pattern' && (
            <>
              {facets.length > 0 && (
                <Field label="Facets">
                  <div className="flex flex-wrap gap-1.5">
                    {facets.map(([k, v], i) => (
                      <Chip key={`${k}${i}`}><span className="text-slate-400">{k}</span> {v}</Chip>
                    ))}
                  </div>
                </Field>
              )}
              {typeof d.structure === 'string' && d.structure && <Field label="Structure">{d.structure}</Field>}
              {typeof d.best_for === 'string' && d.best_for && <Field label="Best for">{d.best_for}</Field>}
              {slots.length > 0 && (
                <Field label="Slots">
                  <div className="flex flex-wrap gap-1.5">
                    {slots.map((s) => <Chip key={s} mono>{`{${s}}`}</Chip>)}
                  </div>
                </Field>
              )}
              {typeof d.recreation_prompt === 'string' && d.recreation_prompt && (
                <Field label="Recreation prompt (slots bound at handoff; never guessed)">
                  <pre className="whitespace-pre-wrap font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 text-slate-700">{d.recreation_prompt}</pre>
                </Field>
              )}
              {item.evidence.length > 0 && <Field label="Evidence">{item.evidence.join(' · ')}</Field>}
            </>
          )}

          {detail.merged_from.length > 0 && (
            <Field label={`Merged from (${detail.merged_from.length} client pack item${detail.merged_from.length === 1 ? '' : 's'})`}>
              <div className="flex flex-wrap gap-1.5">
                {detail.merged_from.map((m) => <Chip key={m} mono>{m}</Chip>)}
              </div>
            </Field>
          )}

          {item.entity_class !== 'market_concept' && (
            <Section
              title={`Exemplar ads (${detail.exemplars.length}${detail.exemplars.length ? `, ${resolvedExemplars} resolved` : ''})`}
              right={<span className="text-[10px] text-slate-400">internal review only</span>}
            >
              {!detail.exemplar_index_available && detail.exemplars.length > 0 && (
                <div className="mb-3">
                  <WarningBanner>The exemplar index for this library is not available yet — showing ad keys only. Images, copy and lens appear once it is uploaded.</WarningBanner>
                </div>
              )}
              {detail.exemplars.length === 0 ? (
                <Empty>This item cites no exemplar ads.</Empty>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {detail.exemplars.map((r) => (
                    <ExemplarCard key={r.ad_key} record={r} onOpen={setLightbox} />
                  ))}
                </div>
              )}
            </Section>
          )}

          {detail.related.length > 0 && (
            <Section title="Related items (shared exemplar ads)">
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
                {detail.related.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => onOpenItem(r.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50">
                      <ClassBadge entityClass={r.entity_class} />
                      <span className="text-sm text-slate-800 flex-1 truncate">{r.name}</span>
                      <span className="font-mono text-[10px] text-slate-400">{r.id}</span>
                      <span className="text-xs text-slate-500 tabular-nums">{r.shared_ads} shared</span>
                    </button>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          <Section title="Usage">
            {!usage && usageLoading && <Loading label="Scanning member briefs…" />}
            {!usage && !usageLoading && <Empty>Usage not loaded.</Empty>}
            {usage && (
              <div className="space-y-4">
                <UnavailableMembers unavailable={usage.unavailable} />
                <div>
                  <div className="text-xs text-slate-500 mb-1">Angles citing this item ({angles.length})</div>
                  <AnglesTable angles={angles} onOpenItem={onOpenItem} />
                </div>
                {item.entity_class === 'creative_pattern' && (
                  <div>
                    <div className="text-xs text-slate-500 mb-1">Handoffs that executed this template ({handoffs.length})</div>
                    <HandoffsTable handoffs={handoffs} onOpenItem={onOpenItem} />
                  </div>
                )}
              </div>
            )}
          </Section>

          <Field label="Admin note (kept with the hide/unhide state)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={() => {
                if ((item.note ?? '') !== note) save(item.hidden, note);
              }}
              rows={2}
              placeholder="Why this item is hidden, or anything the next reviewer should know"
              className="w-full text-sm rounded-lg border border-slate-200 px-3 py-2 focus:outline-none focus:border-brand-teal/50"
            />
          </Field>

          {item.embedded_text && <div className="text-[11px] text-slate-400 font-mono">{item.embedded_text}</div>}
        </div>
      )}
      <ExemplarLightbox record={lightbox} onClose={closeLightbox} onOpenItem={onOpenItem} />
    </SlideOver>
  );
}
